/**
 * useSessionToken
 *
 * The one place the hooks resolve "which bearer token does this request
 * carry?". By default it is the identity provider's token for the signed-in
 * user, minted against the configured template. When a session token override
 * is installed (`setSessionTokenOverride`), that wins — the admin impersonation
 * frame uses it so every request resolves as the impersonated user.
 *
 * Returns a stable async getter so it can sit in `queryFn` closures and
 * `useCallback` dependency lists without churning.
 */

import { useAuth } from "@clerk/clerk-react";
import { useCallback } from "react";

import { getSessionTokenOverride } from "../session-token";
import { getClientConfig } from "../types";

export function useSessionToken(): () => Promise<string | null> {
  const { getToken } = useAuth();

  return useCallback(async () => {
    const override = getSessionTokenOverride();
    if (override) return override();
    return getToken({ template: getClientConfig().tokenTemplate });
  }, [getToken]);
}
